/**
 * Everything §13 asks to be checked, in one place.
 *
 * One place because the editor checks live and the render worker checks again
 * before the press file is made, and those two must never disagree. A customer
 * told "ready to print" and then sent an email saying otherwise has been let
 * down twice.
 *
 * Levels mean exactly three things:
 *
 *   warn   Advice. The order goes through. §6.3 is emphatic that a warning is
 *          not a wall - a customer who knows their logo is 180 dpi and wants it
 *          anyway is allowed to have it.
 *   stop   The design is incomplete. The customer can fix it, and until they
 *          do, ordering it would print something nobody meant.
 *   block  The file cannot be made at all - a missing font. Nothing the
 *          customer does in the editor resolves it; the shop has to.
 */

import { isGradient } from '../model/types';
import type { Colour, Layer, Page, TrimboxDocument } from '../model/types';
import { flatten } from '../model/types';
import { mmToPt, ptToMm } from '../model/units';
import { MIN_MODULE_MM, grid as qrGrid } from '../codes/qr';
import { qrProblems } from '../codes/qr-types';

export type Level = 'warn' | 'stop' | 'block';

export interface Finding {
  level: Level;
  message: string;
  layerId?: string;
  pageIndex?: number;
}

/** What a picture has to know about itself for the resolution check. */
export interface AssetFacts {
  w: number;
  h: number;
}

export interface Facts {
  /** Pixel dimensions of an asset, or null when it is not there. */
  asset(id: string): AssetFacts | null;
  /** Whether a face is available. Null means "not checked" - the editor case. */
  font?(family: string, weight: number, style: string): boolean;
}

/** Below this, type is legible in a proof and not on paper. §13. */
export const MIN_TYPE_PT = 6;

/** A rule finer than this closes up on press or disappears entirely. §13. */
export const MIN_RULE_PT = 0.25;

export const DPI_GOOD = 200;
export const DPI_POOR = 150;

/**
 * Whether a colour is a rich black - black made from all four inks.
 *
 * It is the right choice for a large solid and the wrong one for small text,
 * where the four plates cannot be held in register and the letters fringe.
 */
function isRichBlack(layer: Layer): boolean {
  const fill = 'fill' in layer ? layer.fill : null;

  if (!fill) return false;

  // A gradient counts if any stop along it is a rich black: the small text
  // sitting on it will fringe wherever that stop lands.
  if (isGradient(fill)) return fill.stops.some((stop) => richInk(stop.colour));

  return richInk(fill);
}

function richInk(colour: Colour): boolean {
  if (colour.space === 'rgb') return false;

  const ink = colour.space === 'spot' ? colour.fallback : colour.value;
  const [c, m, y, k] = ink;

  return k > 0.8 && c + m + y > 0.3;
}

export function preflight(doc: TrimboxDocument, facts: Facts): Finding[] {
  const findings: Finding[] = [];

  doc.pages.forEach((page, pageIndex) => {
    const where = doc.pages.length > 1 ? ` on ${page.name}` : '';

    const add = (level: Level, message: string, layer?: Layer) =>
      findings.push({ level, message, layerId: layer?.id, pageIndex });

    for (const layer of flatten(page.layers)) {
      if (layer.hidden) continue;

      const named = name(layer);

      /* --- things that are not finished ------------------------- */

      if (layer.type === 'placeholder') {
        add('stop', `“${layer.prompt || layer.placeholder}”${where} has not been filled in.`, layer);
        continue;
      }

      if (layer.type === 'text' && layer.text.trim() === '') {
        add('stop', `An empty text box${where} will print as nothing.`, layer);
        continue;
      }

      /* --- type ------------------------------------------------- */

      if (layer.type === 'text') {
        if (facts.font && !facts.font(layer.font.family, layer.font.weight, layer.font.style)) {
          add(
            'block',
            `${named}${where} uses ${layer.font.family}, which we do not have. Please pick another font.`,
            layer
          );
        }

        if (layer.font.size < MIN_TYPE_PT) {
          add(
            'warn',
            `${named}${where} is ${round(layer.font.size)} pt. Below ${MIN_TYPE_PT} pt it will not read on paper.`,
            layer
          );
        }

        if (isRichBlack(layer) && layer.font.size < 12) {
          // Four plates cannot be held in perfect register on small type, and
          // the result is coloured fringes around every letter.
          add(
            'warn',
            `${named}${where} is small text in a rich black. Plain black prints it more crisply.`,
            layer
          );
        }
      }

      /* --- rules and hairlines ---------------------------------- */

      const stroke = 'stroke' in layer ? layer.stroke : null;

      if (stroke && stroke.width > 0 && mmToPt(stroke.width) < MIN_RULE_PT) {
        add(
          'warn',
          `${named}${where} has a ${round(mmToPt(stroke.width))} pt line. Below ${MIN_RULE_PT} pt it may not print at all.`,
          layer
        );
      }

      // A line shape with no stroke is drawn at a default hairline, which is
      // the same problem wearing a different hat.
      if (layer.type === 'shape' && layer.shape === 'line' && !stroke) {
        add('warn', `${named}${where} is a hairline. Give it a thickness so it prints reliably.`, layer);
      }

      /* --- pictures --------------------------------------------- */

      if (layer.type === 'image') {
        const asset = layer.asset ? facts.asset(layer.asset) : null;

        if (!asset && !layer.asset && layer.mask?.frame) {
          // A frame the shop fixed into the design cannot be deleted by the
          // customer, so telling them to delete it would be an instruction
          // they cannot follow.
          add(
            'stop',
            layer.fixed
              ? `A photo is needed${where}. Click the frame and choose one.`
              : `The frame${where} is empty. Add a picture to it, or delete it.`,
            layer
          );
        } else if (!asset) {
          add('stop', `${named}${where} has no picture in it.`, layer);
        } else if (layer.w > 0) {
          const dpi = Math.round(asset.w / (layer.w / 25.4));

          if (dpi < DPI_POOR) {
            add('warn', `${named}${where} is only ${dpi} dpi at this size and will print blurred.`, layer);
          } else if (dpi < DPI_GOOD) {
            add('warn', `${named}${where} is ${dpi} dpi at this size and may print a little soft.`, layer);
          }
        }
      }

      /* --- codes ------------------------------------------------ */

      if (layer.type === 'qr') {
        // Advice about what it holds: an email address with no @ scans
        // perfectly and sends every message nowhere.
        if (layer.fields && Object.keys(layer.fields).length > 0) {
          for (const problem of qrProblems(layer.kind, layer.fields)) {
            add('warn', `The QR code${where}: ${problem}`, layer);
          }
        }

        if (layer.value.trim() === '') {
          add('stop', `The QR code${where} has nothing in it.`, layer);
        } else {
          const built = qrGrid(layer);

          if (!built) {
            add(
              'stop',
              `The QR code${where} holds more than will fit. Shorten it, or lower the repair level.`,
              layer
            );
          } else {
            const module = Math.min(layer.w, layer.h) / built.count;

            if (module < MIN_MODULE_MM) {
              add(
                'warn',
                `The QR code${where} has ${module.toFixed(
                  2
                )} mm squares and may not scan once printed. Make it larger or shorten what it holds.`,
                layer
              );
            }
          }
        }
      }

      /* --- where it sits ---------------------------------------- */

      // Content in the bleed is fine. That is what bleed is for, and warning
      // about it would train customers to ignore this list. §13.
      if (matters(layer) && outsideSafe(layer, page.size, doc.safe, page.round)) {
        add('warn', `${named}${where} is close to the cut and may be trimmed off.`, layer);
      }

      /*
       * Hardware swallows part of some sheets - a roll-up's bottom goes into
       * the cassette and its top under the rail, and both are printed. The
       * customer is designing on the whole printed height, so the only thing
       * standing between a logo and the inside of a cassette is this warning.
       */
      const swallowed = hiddenShare(layer, page.shade);

      if (swallowed.share > 0.5) {
        /*
         * The band's name is whatever the shop typed - "Into the cassette",
         * "Under the rail" - so it is quoted rather than read into the
         * sentence. Dropped in bare it produced "sits entirely into the
         * cassette", which is not English and reads as a bug.
         */
        const part = swallowed.name ? `the part marked “${swallowed.name}”` : 'a part the hardware covers';

        add(
          'warn',
          swallowed.share > 0.98
            ? `${named}${where} sits in ${part} and will not be seen.`
            : `${named}${where} runs into ${part} and will be largely hidden.`,
          layer
        );
      }
    }
  });

  const rank: Record<Level, number> = { block: 0, stop: 1, warn: 2 };

  // Worst first: someone reading one line should read the one that matters.
  return findings.sort((a, b) => rank[a.level] - rank[b.level]);
}

/** Whether anything here stops the order going through. */
export function blocking(findings: Finding[]): boolean {
  return findings.some((finding) => finding.level !== 'warn');
}

/**
 * Whether being trimmed would lose something.
 *
 * Only checked for things that carry meaning. A background rectangle is
 * supposed to run past the trim.
 */
function outsideSafe(layer: Layer, size: { w: number; h: number }, safe: number, round = false): boolean {
  /*
   * A circle's safe area is a smaller circle, so the test is a distance rather
   * than four edges. The corner of the layer's box furthest from the centre is
   * what decides it - on a round medal insert the box test would pass artwork
   * that the cutter takes a bite out of, because the corners of the page are
   * not on the product at all.
   */
  if (round) {
    const cx = size.w / 2;
    const cy = size.h / 2;
    const keep = Math.min(size.w, size.h) / 2 - safe;

    if (keep <= 0) return true;

    const dx = Math.max(Math.abs(layer.x - cx), Math.abs(layer.x + layer.w - cx));
    const dy = Math.max(Math.abs(layer.y - cy), Math.abs(layer.y + layer.h - cy));

    return Math.hypot(dx, dy) > keep;
  }

  return (
    layer.x < safe ||
    layer.y < safe ||
    layer.x + layer.w > size.w - safe ||
    layer.y + layer.h > size.h - safe
  );
}

/**
 * How much of a layer a hardware band hides, and which band hides most of it.
 *
 * Measured by area rather than by overlap, because a background is *supposed*
 * to run into the hidden part: a full-bleed picture on a roll-up loses its
 * bottom 180 mm to the cassette and that is exactly right. Warning about that
 * would train customers to ignore this list, which is the one thing a preflight
 * warning must never do. What is wrong is a logo that is *in* the cassette.
 */
function hiddenShare(
  layer: Layer,
  shade: Page['shade']
): { share: number; name: string } {
  const area = layer.w * layer.h;

  if (!shade?.length || area <= 0) return { share: 0, name: '' };

  let total = 0;
  let worst = 0;
  let name = '';

  for (const band of shade) {
    const across = Math.max(0, Math.min(layer.x + layer.w, band.x + band.w) - Math.max(layer.x, band.x));
    const down = Math.max(0, Math.min(layer.y + layer.h, band.y + band.h) - Math.max(layer.y, band.y));
    const covered = across * down;

    total += covered;

    if (covered > worst) {
      worst = covered;
      name = band.name || name;
    }
  }

  // Bands are not meant to overlap, but a shop can define two that do.
  return { share: Math.min(1, total / area), name };
}

function matters(layer: Layer): boolean {
  return layer.type === 'text' || layer.type === 'qr' || layer.type === 'barcode';
}

function name(layer: Layer): string {
  if (layer.name) return `“${layer.name}”`;

  switch (layer.type) {
    case 'text':
      return 'Some text';
    case 'image':
      return 'A picture';
    case 'shape':
      return 'A shape';
    default:
      return 'An object';
  }
}

const round = (value: number) => Math.round(value * 100) / 100;

/** Millimetres from points, re-exported so callers need one import. */
export { ptToMm };
